import { SEV_COLOR, getRegionLabel, SEV_LABEL } from '@/constants'
import { useStore } from '@/store'

export type IncidentPanelRow = {
  id: number | string
  name: string
  msg: string
  since: string
  region: string
  sev: 0 | 1 | 2
}

function SectionHead({ children, col }: { children: React.ReactNode; col: { t2: string } }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: col.t2, marginBottom: 8,
    }}>
      {children}
    </div>
  )
}

function formatSince(iso: string): string {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return '—'
  const mins = Math.max(0, Math.round((Date.now() - t) / 60_000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.round(mins / 60)
  return `${hrs}h`
}

export default function IncidentsPanel({
  incidents,
  onDrill,
  card,
  col,
}: {
  incidents: IncidentPanelRow[]
  onDrill: (region: string) => void
  card: React.CSSProperties
  col: { t1: string; t2: string; bd: string }
}) {
  const setView = useStore((s) => s.setView)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, ...card, overflow: 'hidden', flex: 1, minHeight: 0, height: '100%' }}>
      <div style={{ padding: '10px 12px 8px', borderBottom: `0.5px solid ${col.bd}` }}>
        <SectionHead col={col}>Unresolved · {incidents.length}</SectionHead>
        <button
          type="button"
          onClick={() => setView('incidents')}
          style={{
            fontSize: 10, color: 'var(--info)', background: 'transparent',
            border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font)',
          }}
        >
          Open all incidents →
        </button>
      </div>
      <div style={{ overflowY: 'auto', flex: 1, padding: '6px 0' }}>
        {incidents.length === 0 && (
          <div style={{ padding: '12px', fontSize: 11, color: col.t2, textAlign: 'center' }}>
            All streams healthy
          </div>
        )}
        {incidents.map((inc) => (
          <div
            key={inc.id}
            onClick={() => onDrill(inc.region)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: `0.5px solid ${col.bd}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <div style={{
                width: 4, height: 4, borderRadius: '50%',
                background: SEV_COLOR[String(inc.sev)],
                flexShrink: 0,
                boxShadow: `0 0 5px ${SEV_COLOR[String(inc.sev)]}88`,
              }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: col.t1, flex: 1, letterSpacing: '-0.01em' }}>
                {inc.name}
              </span>
              <span style={{ fontSize: 9, color: col.t2 }}>{inc.since || formatSince(String(inc.id))}</span>
            </div>
            <div style={{ fontSize: 10, color: col.t2, paddingLeft: 12, lineHeight: 1.4 }}>
              {inc.msg}
            </div>
            <div style={{ fontSize: 9, color: SEV_COLOR[String(inc.sev)], paddingLeft: 12, marginTop: 2, opacity: 0.8 }}>
              {getRegionLabel(inc.region)} · {SEV_LABEL[String(inc.sev)]}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
